import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import { fromJS } from 'immutable';
import { withCookies } from 'react-cookie';
import RadioOptionSelector from '@client/components/selectors/radioOptionSelector/RadioOptionSelector';
import Input from '@client/components/formComponents/input/Input';
import InputGroup from '@client/components/formComponents/inputGroup/InputGroup';
import FormWrapper from '@client/components/formComponents/formWrapper/FormWrapper';
import { NotificationContext } from '@client/components/notificationProvider/NotificationProvider';
import {
  capitalizeFirstLetter,
  fakeEllipsis,
  remapObjValueToString,
  valueOrEmptyStr,
  guid,
} from '@client/shared/utils/utils/utils';
import {
  selectDonationInProgress,
  setMakeDonationInProgress,
} from '@client/redux/donation/donationReducer';
import Checkbox from '@client/components/formComponents/checkbox/Checkbox';
import { getIn } from '@client/shared/utils/immutableUtils/immutableUtils';
import styleHelper from '@client/shared/styles/styleHelper.scss';
import SoftOptInPhoneNumberPolicyText from '@client/containers/softOptInPhoneNumberPolicyText/SoftOptInPhoneNumberPolicyText';
import PolicyText from '@client/containers/policyText/PolicyText';
import FormSectionContainer from '@client/containers/formSectionContainer/FormSectionContainer';
import { selectTranslations } from '@client/redux/i18n';
import {
  selectFundraiserById,
  selectFundraiserStatus,
} from '@client/redux/fundraiser/fundraiserSelectors';
import { loadFundraiser } from '@client/redux/fundraiser/fundraiserActions';
import { session } from '@client/shared/utils/session/session';
import { isMemorialFundraiser } from '@client/shared/utils/fundraiserUtils/fundraiserUtils';
import { donateFundraiserSchema } from '@client/shared/utils/validation/validationSchemas/donateFundraiserSchema';
import { trackFormStart } from '@client/tracking/GTM';
import { GIFT_TYPE_FUNDRAISER_DONATION } from '@client/definitions/tracking.js';
import {
  COMPANY_NAME,
  SSN_ORG_NUMBER,
} from '@client/definitions/formFieldNames';
import { postDonation } from '@client/connectivity/api.donation';
import { addNEIOrder } from '@client/connectivity/api.frontend';
import { LOCAL_HLF_FRONTEND_URL } from '@client/connectivity/baseRequestConfig';
import { reverseRoutesFullpath } from '@client/pages/reverseRoutes';
import {
  ERROR,
  AN_ERROR_OCCURRED,
  DONATION_NEWSLETTER_LABEL,
} from '@client/definitions/constants.js';
import styles from './donateFundraiserRoute.scss';
import NotFoundPage from '@client/pages/notFoundPage/NotFoundPage';

const selectableOptions = fromJS([
  {
    id: '1',
    label: '100 kr',
    value: '100',
  },
  {
    id: '2',
    label: '300 kr',
    value: '300',
  },
  {
    id: '3',
    label: '1000 kr',
    value: '1000',
  },
]);

const defaultOption = selectableOptions.get(1);

const individualTypeOptions = fromJS([
  {
    id: '1',
    label: 'Privatperson',
    value: 'Person',
  },
  {
    id: '2',
    label: 'Företag',
    value: 'Company',
  },
]);

const defaultIndividualType = individualTypeOptions.get(0);

class DonationForm extends PureComponent {
  constructor(props) {
    super(props);
    this.state = { formStarted: false };
  }

  setAmount = (amount) => {
    this.props.formProps.setFieldValue('amount', amount);
  };

  componentDidMount() {
    this.setAmount(defaultOption.get('value'));
  }

  componentDidUpdate(prevProps) {
    const { formProps } = this.props;

    if (
      formProps?.values?.individualType !==
      prevProps?.formProps?.values?.individualType
    ) {
      formProps.setFieldValue(COMPANY_NAME, undefined);
      formProps.setFieldValue(SSN_ORG_NUMBER, undefined);
    }

    if (
      formProps?.values?.optionAmount &&
      formProps?.values?.optionAmount !==
        prevProps?.formProps?.values?.optionAmount
    ) {
      formProps.setFieldValue('inputAmount', '');
      this.setAmount(formProps?.values?.optionAmount);
    }
  }

  handleFormStart = () => {
    const { formStarted } = this.state;
    if (formStarted) return;
    trackFormStart(GIFT_TYPE_FUNDRAISER_DONATION);
    this.setState({ formStarted: true });
  };

  handleInputAmountChange = (event) => {
    const value = event.target.value;

    this.props.formProps.setFieldValue('inputAmount', value);
    this.props.formProps.setFieldValue('optionAmount', '');
    this.setAmount(value);
  };

  render() {
    const { className, formProps, donationInProgress } = this.props;
    const formValues = formProps.values;
    const formErrors = formProps.errors;
    const inputAmount = valueOrEmptyStr(formValues.inputAmount);
    const isCompany = formValues.individualType === 'Company';

    return (
      <form
        onSubmit={formProps.handleSubmit}
        className={`${styles.donationForm}${className ? ` ${className}` : ''}`}
        onClick={this.handleFormStart}
      >
        <FormSectionContainer number={1} title={'Ge en gåva'}>
          <RadioOptionSelector
            label="Jag är"
            name={'individualType'}
            value={formValues.individualType}
            options={individualTypeOptions}
          />

          <RadioOptionSelector
            label="Välj summa"
            name={'optionAmount'}
            value={formValues.optionAmount}
            options={selectableOptions}
          >
            <Input
              type="number"
              placeholder="Valfri summa"
              name={'inputAmount'}
              value={inputAmount}
              onChange={this.handleInputAmountChange}
            />
          </RadioOptionSelector>
        </FormSectionContainer>
        <FormSectionContainer
          number={2}
          title={isCompany ? 'Företagsuppgifter' : 'Mina uppgifter'}
        >
          <div>
            {isCompany && (
              <div className={styleHelper.formRow}>
                <InputGroup
                  labelText={'Företag*'}
                  name={'companyName'}
                  value={valueOrEmptyStr(formValues.companyName)}
                  onChange={formProps.handleChange}
                  onBlur={formProps.handleBlur}
                  errorLabel={formErrors.companyName}
                />
                <InputGroup
                  labelText={'Organisationsnummer'}
                  name={'ssnOrgNumber'}
                  value={valueOrEmptyStr(formValues.ssnOrgNumber)}
                  onChange={formProps.handleChange}
                  onBlur={formProps.handleBlur}
                  errorLabel={formErrors.ssnOrgNumber}
                />
              </div>
            )}

            <div className={styleHelper.formRow}>
              <InputGroup
                labelText={`Förnamn${isCompany ? ' (Kontaktperson)' : ''}*`}
                name={'firstName'}
                value={valueOrEmptyStr(formValues.firstName)}
                onChange={formProps.handleChange}
                onBlur={formProps.handleBlur}
                errorLabel={formErrors.firstName}
              />
              <InputGroup
                labelText={`Efternamn${isCompany ? ' (Kontaktperson)' : ''}*`}
                name={'lastName'}
                value={valueOrEmptyStr(formValues.lastName)}
                onChange={formProps.handleChange}
                onBlur={formProps.handleBlur}
                errorLabel={formErrors.lastName}
              />
            </div>
            <div className={styleHelper.formRow}>
              <InputGroup
                labelText={'Adress*'}
                name={'streetAddress'}
                value={valueOrEmptyStr(formValues.streetAddress)}
                onChange={formProps.handleChange}
                onBlur={formProps.handleBlur}
                errorLabel={formErrors.streetAddress}
                size="2.1"
              />
              <InputGroup
                className={`${styles.post}`}
                labelText={'Postnummer*'}
                name={'zipCode'}
                value={valueOrEmptyStr(formValues.zipCode)}
                onChange={formProps.handleChange}
                onBlur={formProps.handleBlur}
                errorLabel={formErrors.zipCode}
              />
              <InputGroup
                className={`${styles.city}`}
                labelText={'Ort*'}
                name={'city'}
                value={valueOrEmptyStr(formValues.city)}
                onChange={formProps.handleChange}
                onBlur={formProps.handleBlur}
                errorLabel={formErrors.city}
              />
            </div>
            <div className={styleHelper.formRow}>
              <InputGroup
                labelText={'Telefonnummer (ej obligatoriskt)'}
                name={'phoneNumber'}
                value={valueOrEmptyStr(formValues.phoneNumber)}
                onChange={formProps.handleChange}
                onBlur={formProps.handleBlur}
                errorLabel={formErrors.phoneNumber}
              />
              <InputGroup
                labelText={`E-post${isCompany ? ' (Kontaktperson)' : ''}*`}
                name={'email'}
                value={valueOrEmptyStr(formValues.email)}
                onChange={formProps.handleChange}
                onBlur={formProps.handleBlur}
                errorLabel={formErrors.email}
              />
            </div>
            <div className={styles.softOptInPolicyTextContainer}>
              <SoftOptInPhoneNumberPolicyText />
            </div>
            <div className={styles.checkbox}>
              <Checkbox
                name={'newsletter'}
                isChecked={formValues.newsletter}
                rightLabel={DONATION_NEWSLETTER_LABEL}
                onChange={formProps.handleChange}
                onBlur={formProps.handleBlur}
              />
            </div>
            <div className={styles.checkbox}>
              <Checkbox
                name={'anonymousDonation'}
                isChecked={formValues.anonymousDonation}
                rightLabel={'Jag vill vara anonym'}
                onChange={formProps.handleChange}
                onBlur={formProps.handleBlur}
              />
              <p>
                Ditt namn kommer inte att synas på insamlingssidan men
                Hjärt-Lungfonden behöver fortfarande ditt namn.
              </p>
            </div>
          </div>
          <div className={`${styles.submitContainer}`}>
            <PolicyText />
            <button
              type="submit"
              disabled={donationInProgress}
              className={`${styleHelper.btnPrimaryLg} ${styles.donateButton}`}
            >
              Fortsätt
            </button>
          </div>
        </FormSectionContainer>
      </form>
    );
  }
}

class DonatePage extends PureComponent {
  constructor(props) {
    super(props);
    this.state = {
      paymentCompleted: '',
    };
  }

  static contextType = NotificationContext;

  static loadData(dispatch, urlLocation, matchParams) {
    return dispatch(loadFundraiser(matchParams.fundraiserId));
  }

  componentDidMount() {
    DonatePage.loadData(
      this.props.dispatch,
      this.props.location,
      this.props.match.params
    );
  }

  handleSubmit = (values, { setErrors }) => {
    const { match, dispatch, donationInProgress } = this.props;
    const fundraiserId = match.params.fundraiserId;
    const fieldValues = donateFundraiserSchema().cast(values);
    const { cookies } = this.props;

    const handleError = (error) => {
      return this.context.displayNotification(
        ERROR,
        error ? error : AN_ERROR_OCCURRED
      );
    };

    const makeDonation = ({ values, fundraiserId, session }) => {
      return function (dispatch) {
        dispatch(setMakeDonationInProgress(true));
        return postDonation(values, fundraiserId)
          .then((res) => {
            const amount = values?.amount;
            const donationId = res?.data?.id;
            const campaignCode = res?.data?.campaignCode;

            // Build WebNEIOrderRegistration for FrontEnd.API
            const isCompany =
              values?.individualType && values.individualType === 'Company';

            const neiOrder = {
              FoundId: fundraiserId,
              DonationId: donationId,
              Amount: String(amount || ''),
              Anonymous: !!values.anonymousDonation,
              CampaignCode: campaignCode || '',
              Payer: isCompany
                ? {
                    Company: {
                      Name: valueOrEmptyStr(values.companyName),
                      OrgNumber: valueOrEmptyStr(values.ssnOrgNumber),
                      FirstName: valueOrEmptyStr(values.firstName),
                      LastName: valueOrEmptyStr(values.lastName),
                      Address: valueOrEmptyStr(values.streetAddress),
                      Zip: valueOrEmptyStr(values.zipCode),
                      City: valueOrEmptyStr(values.city),
                      Email: valueOrEmptyStr(values.email),
                      PhoneNumber: valueOrEmptyStr(values.phoneNumber),
                      Newsletter: !!values.newsletter,
                    },
                  }
                : {
                    Person: {
                      FirstName: valueOrEmptyStr(values.firstName),
                      LastName: valueOrEmptyStr(values.lastName),
                      Address: valueOrEmptyStr(values.streetAddress),
                      Zip: valueOrEmptyStr(values.zipCode),
                      City: valueOrEmptyStr(values.city),
                      Email: valueOrEmptyStr(values.email),
                      PhoneNumber: valueOrEmptyStr(values.phoneNumber),
                      Newsletter: !!values.newsletter,
                    },
                  },
              Tracking: {
                TrackingCode: campaignCode || '',
                Identity: guid(),
                Session: guid(),
                CurrentUrl: session.currentUrl,
                Type: 'NEIRegularGift',
              },
            };

            // 1) Add NEI order to FrontEnd.API to create order and get orderReference
            return addNEIOrder(neiOrder)
              .then(({ data }) => {
                // API may return either plain GUID, quoted GUID string, or an object
                let rawOrderReference = data;

                if (
                  rawOrderReference &&
                  typeof rawOrderReference === 'object'
                ) {
                  rawOrderReference =
                    rawOrderReference.orderReference ||
                    rawOrderReference.OrderReference ||
                    rawOrderReference.data?.orderReference ||
                    rawOrderReference.data?.OrderReference;
                }

                let orderReference = (rawOrderReference ?? '')
                  .toString()
                  .trim()
                  .replace(/[\r\n]/g, '');

                if (
                  (orderReference.startsWith('"') &&
                    orderReference.endsWith('"')) ||
                  (orderReference.startsWith("'") &&
                    orderReference.endsWith("'"))
                ) {
                  orderReference = orderReference.slice(1, -1).trim();
                }

                if (!orderReference) {
                  handleError();
                  return;
                }

                // 2) Redirect to new PaymentRequests flow
                const successUrl = reverseRoutesFullpath.donationConfirmation({
                  donationId,
                });

                const paymentRequestUrl = `${LOCAL_HLF_FRONTEND_URL}/bz/PaymentRequests/${orderReference}?successurl=${encodeURIComponent(
                  successUrl
                )}`;

                window.location.href = paymentRequestUrl;
              })
              .catch(() => handleError());
          })
          .then((res) => {
            return res;
          })
          .catch((error) => handleError(error.detail))
          .finally(() => {
            setInterval(() => {
              dispatch(setMakeDonationInProgress(false));
            }, 2000);
          });
      };
    };

    if (!donationInProgress) {
      dispatch(
        makeDonation({
          fundraiserId,
          values: fieldValues,
          session: session(cookies),
        })
      ).catch(({ errors }) => {
        if (errors) {
          setErrors(remapObjValueToString(errors));
        }
      });
    }
  };

  renderForm = (formProps) => {
    return (
      <DonationForm
        className={styles.donationFormProp}
        formProps={formProps}
        donationInProgress={this.props.donationInProgress}
      />
    );
  };

  addSuffixS = (name) => {
    if (typeof name !== 'string' || !name) {
      return '';
    }
    if (!name.endsWith('s')) {
      return `${name}s`;
    } else {
      return name;
    }
  };

  render() {
    const { fundraiser, translations, fundraiserStatus } = this.props;

    // Render 404 page when fundraiser is missing or failed to load
    if (fundraiserStatus === ERROR || !fundraiser) {
      return <NotFoundPage />;
    }

    const isUnknown = getIn(fundraiser, 'creator', 'unknown');
    const fundraiserTitle = fakeEllipsis(getIn(fundraiser, 'title'), 32);
    const isMemorialFund = isMemorialFundraiser(
      getIn(fundraiser, 'fundraiserType')
    );
    const firstName = this.addSuffixS(
      getIn(fundraiser, 'creator', 'firstName')
    );

    return (
      <div
        className={`${styles.donateFundraiserRoute} ${styleHelper.contentContainer}`}
      >
        <h3 className={styles.donateTitle}>
          Bidra till{' '}
          {isUnknown
            ? `${
                isMemorialFund ? 'minnesfonden' : 'insamlingen'
              } ${fundraiserTitle}`
            : `${firstName} insamling`}
        </h3>
        <FormWrapper
          initialValues={{
            optionAmount: defaultOption.get('value'),
            individualType: defaultIndividualType.get('value'),
            newsletter: false,
            anonymousDonation: false,
          }}
          onSubmit={this.handleSubmit}
          component={this.renderForm}
          validateOnBlur={false}
          validateOnChange={false}
          validationSchema={donateFundraiserSchema()}
          transformationSchema={{
            companyName: capitalizeFirstLetter,
            firstName: capitalizeFirstLetter,
            lastName: capitalizeFirstLetter,
            streetAddress: capitalizeFirstLetter,
            city: capitalizeFirstLetter,
          }}
        />
        <span className={`${styles.requiredInfoText}`}>
          {getIn(translations, 'FORM_REQUIRE_FIELD_EXPLANATION')}
        </span>
      </div>
    );
  }
}

DonatePage.propTypes = {
  dispatch: PropTypes.func,
  location: PropTypes.object,
  cookies: PropTypes.object,
  match: PropTypes.object,
  donationInProgress: PropTypes.bool,
  translations: ImmutablePropTypes.map,
  fundraiser: ImmutablePropTypes.map,
};

function mapStateToProps(state, props) {
  const fundraiserId = props.match.params && props.match.params.fundraiserId;

  return {
    fundraiser: selectFundraiserById(state, fundraiserId.toString()),
    translations: selectTranslations(state),
    donationInProgress: selectDonationInProgress(state),
    fundraiserStatus: selectFundraiserStatus(state),
  };
}

export default connect(mapStateToProps)(withCookies(DonatePage));
